import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LogIn, Gamepad2, BookCheck, ClipboardCheck, Search,
  ChevronLeft, ChevronRight, Loader2, Filter, Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_CONFIG = {
  login:                { icon: LogIn,          color: "text-blue-600 bg-blue-50",   label: "Login" },
  game_hosted:          { icon: Gamepad2,       color: "text-purple-600 bg-purple-50", label: "Game Hosted" },
  game_started:         { icon: Gamepad2,       color: "text-indigo-600 bg-indigo-50", label: "Game Started" },
  lesson_completed:     { icon: BookCheck,      color: "text-green-600 bg-green-50",  label: "Lesson Completed" },
  module_completed:     { icon: BookCheck,      color: "text-emerald-600 bg-emerald-50", label: "Module Completed" },
  assessment_completed: { icon: ClipboardCheck, color: "text-amber-600 bg-amber-50", label: "Assessment" },
};

const ROLE_FILTERS = [
  { value: "all", label: "All" },
  { value: "student", label: "Students" },
  { value: "educator", label: "Educators" },
  { value: "admin", label: "Admins" },
];

function roleBadge(role) {
  const cls = role === "admin" ? "bg-red-100 text-red-700"
    : role === "educator" ? "bg-blue-100 text-blue-700"
    : "bg-green-100 text-green-700";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>{role || "student"}</span>;
}

function SummaryCards({ summary, loading }) {
  const cards = [
    { label: "Logins Today",    value: summary?.logins_today ?? "—",           color: "bg-blue-50 text-blue-600 border-blue-100" },
    { label: "Active Educators", value: summary?.active_educators ?? "—",      color: "bg-purple-50 text-purple-600 border-purple-100" },
    { label: "Games Today",      value: summary?.games_hosted_today ?? "—",    color: "bg-indigo-50 text-indigo-600 border-indigo-100" },
    { label: "Modules Completed", value: summary?.modules_completed_today ?? "—", color: "bg-green-50 text-green-600 border-green-100" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {cards.map((c, i) => (
        <div key={i} className={`rounded-xl border p-4 ${c.color}`} data-testid={`summary-${i}`}>
          <div className="text-xs font-medium opacity-70 mb-1">{c.label}</div>
          <div className="text-2xl font-bold">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : c.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function ActivityFeed() {
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/activity-summary`, { headers });
      setSummary(res.data);
    } catch (e) { console.error(e); }
    finally { setSummaryLoading(false); }
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ role_filter: roleFilter, page, page_size: 30 });
      if (search) params.set("search", search);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await axios.get(`${API}/admin/activity-feed?${params}`, { headers });
      setActivities(res.data.activities || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [roleFilter, search, dateFrom, dateTo, page]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div>
      <SummaryCards summary={summary} loading={summaryLoading} />

      {/* Filters Row */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          {/* Role filter pills */}
          <div className="flex items-center gap-1" data-testid="role-filters">
            <Filter className="w-4 h-4 text-gray-400 mr-1" />
            {ROLE_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => { setRoleFilter(f.value); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  roleFilter === f.value
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2 w-full md:w-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, module, class..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                data-testid="activity-search"
              />
            </div>
            <button type="submit" className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-800">Go</button>
          </form>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid="date-from" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid="date-to" />
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No activity found</div>
        ) : (
          <>
            <div className="divide-y divide-gray-100" data-testid="activity-list">
              {activities.map((a, i) => {
                const cfg = ACTION_CONFIG[a.action] || ACTION_CONFIG.login;
                const Icon = cfg.icon;
                const ts = a.timestamp ? new Date(a.timestamp) : null;
                const detail = a.module_name || a.class_name || a.method || "";
                const playerCount = a.details?.player_count;

                return (
                  <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors" data-testid={`activity-row-${i}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-900 truncate">{a.user_name || "Unknown"}</span>
                        {roleBadge(a.user_role)}
                        <span className="text-xs text-gray-500">{cfg.label}</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                        {a.method && <span>via {a.method}</span>}
                        {detail && <span>{detail}</span>}
                        {playerCount !== undefined && <span>{playerCount} players</span>}
                        {a.details?.game_code && <span>Code: {a.details.game_code}</span>}
                        {a.details?.game_mode && <span>Mode: {a.details.game_mode}</span>}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 shrink-0 text-right">
                      {ts ? (
                        <>
                          <div>{ts.toLocaleDateString()}</div>
                          <div>{ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                        </>
                      ) : "—"}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-500">{total} total activities</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                  data-testid="page-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-600">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                  data-testid="page-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
